// Las medidas y la ficha que se tocan en el Catálogo se guardan también en el producto (Mi marca),
// con debounce por clave (`m:<varianteId>`, `f:<varianteId>`). Un guardado que falla se reintenta
// una vez; si vuelve a fallar queda anotado hasta que otro de la misma clave salga bien o se
// reintente a mano, y la pantalla lo muestra (review 2026-10-15: antes el resultado se tiraba y
// el agente decía "guardado"). Cada guardado lleva su versión: uno viejo que espera o se reintenta
// nunca pisa a uno más nuevo. Las versiones son de TODA la app (la clave ya dice la variante): el
// reintento de un catálogo que se cerró tampoco pisa lo que otro catálogo guardó después del mismo
// producto. Puro salvo `esperar` y las versiones, inyectables.

export type GuardarEnProducto = () => Promise<{ ok: boolean }>;

const VERSIONES_DE_LA_APP = new Map<string, number>();

export class GuardadoEnProducto {
  private fallidos = new Map<string, { varianteId: string; fn: GuardarEnProducto }>();

  constructor(
    /** Los varianteId que quedaron sin guardar, cada vez que cambian. */
    private alCambiar: (sinGuardar: string[]) => void,
    private esperar: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
    private tiempos = { debounceMs: 900, reintentoMs: 2_000 },
    private versiones: Map<string, number> = VERSIONES_DE_LA_APP,
  ) {}

  async guardar(clave: string, varianteId: string, fn: GuardarEnProducto, inmediato = false): Promise<void> {
    const version = (this.versiones.get(clave) ?? 0) + 1;
    this.versiones.set(clave, version);
    const vigente = () => this.versiones.get(clave) === version;
    // Lo anotado como sin guardar pasa a ser ESTE valor: "Volver a intentar" nunca manda uno viejo.
    if (this.fallidos.has(clave)) this.fallidos.set(clave, { varianteId, fn });
    if (!inmediato) await this.esperar(this.tiempos.debounceMs);
    for (let intento = 0; intento < 2; intento++) {
      if (intento > 0) await this.esperar(this.tiempos.reintentoMs);
      if (!vigente()) return;
      const ok = await fn().then((r) => r.ok, () => false);
      if (!vigente()) return;
      if (ok) {
        this.fallidos.delete(clave);
        break;
      }
      if (intento === 1) this.fallidos.set(clave, { varianteId, fn });
    }
    this.publicar();
  }

  /** Vuelve a mandar, ya, lo que quedó sin guardar. */
  reintentar(): void {
    for (const [clave, { varianteId, fn }] of [...this.fallidos]) void this.guardar(clave, varianteId, fn, true);
  }

  private publicar(): void {
    this.alCambiar([...new Set([...this.fallidos.values()].map((f) => f.varianteId))]);
  }
}
